import moment from "moment";

import masks from "../../../providers/masks";
import exportationProvider from "../../../providers/exportation.provider";
import { flattenObject, getMissingFields } from "../../../components/providers/utils.provider";

import customerInterface from "../../interfaces/customer.interface";

import { filter } from "../client/filter.service";
import repository from '../../repositories/customer/customer.repository';
import datetimeProvider from "../../../providers/datetime.provider";
import loginService from "../login.service";

const findByFilter = async ({ filters = [], pageIndex, pageSize, sortBy = [] }, activePeriodFilters = []) =>
    filter(
        {
            filters: [
                ...filters.map(({ id, value }) => ({ name: id, key: id, value })),
                ...activePeriodFilters.map(({ name, value }) => ({
                    name,
                    value,
                })),
            ],
            sortBy: sortBy.map(({ id, desc }) => ({
                id: masks.camelToSnakeCase(id),
                desc,
            })),
        },
        { pageIndex, pageSize }
    ).then(res => res.data);

const mapToExcel = (clients) =>
    clients.map((item) => ({
        ID: item.id,
        Nome: item.name,
        Email: item.email,
        Celular: masks.cellPhone(item.telephone),
        "Criado em": item.created && moment.utc(item.created).format("DD/MM/YYYY"),
        "Data da Última Compra":
            item.lastPurchaseDate &&
            moment.utc(item.lastPurchaseDate).local().format("DD/MM/YYYY"),
        "Valor Total Investido": item.totalAmountInvested,
        "Pendência  Financeira": item.isFinancialPending ? "SIM" : "NÃO",
        CPF: item.personType == 'individual' && item.identity ? masks.cpf(item.identity) : '',
        CNPJ: item.personType == 'corporation' && item.identity ? masks.cnpj(item.identity) : '',
        "Nome Fantasia": item.company && item.company.fantasyName,
        "Razão Social": item.company && item.company.companyName,
        "Observações interna":
            item.additionInformation && item.additionInformation.observation,
        Endereço:
            item.address &&
            `${item.address.street}, ${item.address.district}, ${item.address.number}`,
        UF: item.address && `${item.address.state}`,
        Cidade: item.address && `${item.address.city}`,
        Rua: item.address && `${item.address.street}, ${item.address.number}`,
        Bairro: item.address && `${item.address.district}`,
        Complemento: item.address && item.address.complement,
        CEP: item.address && item.address.zipCode,
        "Canal de Aquisição": item.acquisition?.channel,
        "Outro Canal de Aquisição": item.acquisition?.description || ''
    }));

const exportByFilter = async ({ filters, sortBy }) => {

    const exportationParams = { filters, sortBy, pageIndex: 0, pageSize: 99999 }

    return findByFilter(exportationParams)
        .then(data => exportationProvider.exportData(
            mapToExcel(data.results), "clientes",
            [
                { wch: 10 }, { wch: 10 }, { wch: 30 }, { wch: 35 }, { wch: 35 },
                { wch: 50 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 30 },
                { wch: 30 }, { wch: 80 }, { wch: 35 }, { wch: 35 }, { wch: 35 },
                { wch: 35 }, { wch: 35 }, { wch: 20 }, { wch: 20 }, { wch: 35 },
                { wch: 35 }, { wch: 50 }, { wch: 50 }, { wch: 50 }, { wch: 50 },
            ]
        ));
}

const saveAction = async (client) =>
    client.id ? edit(client.id, { ...client }) : repository.saveInternal({ ...client });

const save = async (data) =>
    repository.save(data);

const saveLead = async (data) =>
    repository.saveLead(data);

const removeClient = async (id, data) =>
    repository.removeClient(id, data)

const externalSave = async (data) =>
    repository.externalSave(data);

const edit = async (id, data) => {

    const updateData = await customerInterface.update({ ...data, id });

    if (!updateData)
        return {};

    return repository.edit(id, data);
};

const registerEdition = async (id, data) =>
    repository.registerEdition(id, data);

// Busca no servidor (nome, e-mail ou CPF/CNPJ) para os seletores; a base inteira não vai mais ao navegador.
const search = async ({ q, limit, id } = {}, signal) => {
    const res = await repository.search({ q, limit, id }, signal);
    return Array.isArray(res?.data) ? res.data : [];
};

const FILTER_SEARCH_LIMIT = 50;

// Formato que as listas de filtro ("Clientes") já usam: id + name.
const searchFilterOptions = async (term, signal) =>
    search({ q: term, limit: FILTER_SEARCH_LIMIT }, signal)
        .then(customers => customers.map(({ id, name }) => ({ id, name })));

const findById = async (id) =>
    repository.findById(id);

const findByEmail = async (id) =>
    repository.findByEmail(id);

const findByUUID = async (uuid) =>
    repository.findByUUID(uuid);

const getUUID = async () =>
    repository.getUUID();

const newClient = () => ({ name: "", email: "", telephone: "" });

const getTextFormatRequirement = (id) =>
    repository.getTextFormatRequirement(id);

const getRepurchaseRate = (yearMonth) =>
    repository.getRepurchaseRate(yearMonth);

const getRetentionRate = (period) =>
    repository.getRetentionRate(period);

const getUsageRate = (data) =>
    repository.getUsageRate(data);

const baseRequiredFieldsToUpdate = [
    {
        key: 'name',
        label: 'Nome',
        section: 'account'
    },
    {
        key: 'email',
        label: 'E-mail',
        section: 'account'
    },
    {
        key: 'telephone',
        label: 'Telefone',
        section: 'account'
    },
    {
        key: 'personType',
        label: 'Tipo de Pessoa',
        section: 'account'
    },
    {
        key: 'identity',
        label: 'CPF/CNPJ',
        section: 'account'
    },
    {
        key: "oab",
        label: "OAB",
        section: 'account'
    },
    {
        key: 'zipCode',
        label: 'CEP',
        section: 'address'
    },
    {
        key: 'street',
        label: 'Rua',
        section: 'address'
    },
    {
        key: 'district',
        label: 'Bairro',
        section: 'address'
    },
    {
        key: 'state',
        label: 'UF',
        section: 'address'
    },
    {
        key: 'city',
        label: 'Cidade',
        section: 'address'
    },
    {
        key: 'number',
        label: 'Número',
        section: 'address'
    }
];

const getRequiredFieldsToUpdate = (customer) => {

    if (!customer.id)
        return [
            {
                key: 'name',
                label: 'Nome',
                section: 'account'
            }, {
                key: 'email',
                label: 'E-mail',
                section: 'account'
            },
            {
                key: 'telephone',
                label: 'Telefone',
                section: 'account'
            }
        ];

    const requiredFields = [...baseRequiredFieldsToUpdate];

    if (customer.personType == 'corporation')
        requiredFields.push({ key: 'companyName', label: 'Razão Social', section: 'account' });

    return requiredFields;
}

const checkFieldsToSave = (customer, section) => {

    const requiredFields = getRequiredFieldsToUpdate(customer).filter(field => !section || field.section === section);
    const allFields = flattenObject(customer);

    const missingFields = getMissingFields(allFields, requiredFields.map(f => f.key));

    return requiredFields.filter(rf => missingFields.some(mf => mf == rf.key));
}

const updateAbandonedCheckout = (checkoutInfo, sellerUserId) => {

    if (!loginService.isLogin())
        return;

    const body = {
        abandonedCheckoutInformation: getPurchaseInfo(checkoutInfo, sellerUserId)
    };

    const user = loginService.getUserAuthentication();

    if (!user?.customer?.id)
        return;

    edit(user.customer.id, body).catch(err => null);
}

const getPurchaseInfo = (checkoutInfo, sellerUserId) => {

    return {
        productId: checkoutInfo.product?.id,
        productTypeId: checkoutInfo.product?.productTypeId,
        productType: checkoutInfo.product?.type,
        productName: checkoutInfo.product?.name,
        quantity: checkoutInfo.product?.packageAmount,
        paymentMethod: checkoutInfo.paymentMethod,
        voucherCode: checkoutInfo.voucher?.code,
        date: datetimeProvider.getDate().format('YYYY-MM-DD HH:mm'),
        sellerUserId,
        installments: checkoutInfo.paymentInfo?.installment
    }
}

export default {
    saveAction,
    saveLead,
    save,
    externalSave,
    edit,
    registerEdition,
    removeClient,
    search,
    searchFilterOptions,
    findById,
    findByUUID,
    getUUID,
    newClient,
    getTextFormatRequirement,
    getRepurchaseRate,
    getRetentionRate,
    getUsageRate,
    findByFilter,
    exportByFilter,
    checkFieldsToSave,
    findByEmail,
    updateAbandonedCheckout,
    getPurchaseInfo
};
